import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Clock, CheckCircle, XCircle, Plus, Bell } from 'lucide-react';
import { getMyBookings, getNotifications, markAllRead } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';

const StatusBadge = ({ status }) => <span className={`badge-${status}`}>{status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>;

export default function UserDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [allStats, setAllStats] = useState({ total: 0, upcoming: 0, completed: 0, cancelled: 0 });
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      // Recent 5 for the display list
      getMyBookings({ limit: 5 }).then(r => setBookings(r.data.bookings)),
      // Total count from API pagination metadata
      getMyBookings({ limit: 1 }).then(r =>
        setAllStats(prev => ({ ...prev, total: r.data.total || 0 }))),
      // Completed count
      getMyBookings({ limit: 1, status: 'completed' }).then(r =>
        setAllStats(prev => ({ ...prev, completed: r.data.total || 0 }))),
      // Cancelled count
      getMyBookings({ limit: 1, status: 'cancelled' }).then(r =>
        setAllStats(prev => ({ ...prev, cancelled: r.data.total || 0 }))),
      // Upcoming = pending + confirmed + assigned
      Promise.all([
        getMyBookings({ limit: 1, status: 'pending' }),
        getMyBookings({ limit: 1, status: 'confirmed' }),
        getMyBookings({ limit: 1, status: 'assigned' }),
      ]).then(([p, c, a]) =>
        setAllStats(prev => ({ ...prev, upcoming: (p.data.total || 0) + (c.data.total || 0) + (a.data.total || 0) }))),
      getNotifications().then(r => {
        setNotifications(r.data.notifications);
        setUnread(r.data.unreadCount);
      }),
    ]).finally(() => setLoading(false));
  }, []);

  const handleMarkRead = async () => {
    await markAllRead();
    setUnread(0);
    setNotifications(n => n.map(x => ({ ...x, is_read: true })));
  };

  const stats = [
    { label: 'Total Rides',  value: allStats.total,     icon: Car,         color: 'bg-blue-100 text-blue-700' },
    { label: 'Upcoming',     value: allStats.upcoming,  icon: Clock,       color: 'bg-yellow-100 text-yellow-700' },
    { label: 'Completed',    value: allStats.completed, icon: CheckCircle, color: 'bg-green-100 text-green-700' },
    { label: 'Cancelled',    value: allStats.cancelled, icon: XCircle,     color: 'bg-red-100 text-red-700' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700" /></div>;

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Welcome */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading">Welcome back, {user?.first_name}! 👋</h1>
          <p className="text-gray-500 text-sm mt-1">Here's an overview of your transportation activity.</p>
        </div>
        <Link to="/dashboard/book" className="btn-primary flex items-center gap-2 text-sm"><Plus size={16} /> Book a Ride</Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card border border-gray-100 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon size={20} /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent rides */}
        <div className="lg:col-span-2 card border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900 font-heading">Recent Rides</h2>
            <Link to="/dashboard/rides" className="text-sm text-purple-700 hover:underline">View all →</Link>
          </div>
          {bookings.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Car size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No rides yet.</p>
              <Link to="/dashboard/book" className="btn-primary text-sm mt-4 inline-block">Book Your First Ride</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map(b => (
                <div key={b.uuid} className="flex items-start justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{b.pickup_city || b.pickup_address?.split(' ')[0]} → {b.destination_city || b.destination_address?.split(' ')[0]}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{b.appointment_date} at {b.appointment_time?.slice(0, 5)}</p>
                    <p className="text-xs text-gray-400 mt-0.5 capitalize">{b.service_type?.replace(/_/g, ' ')}</p>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="card border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-gray-900 font-heading">Notifications</h2>
              {unread > 0 && <span className="w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">{unread}</span>}
            </div>
            {unread > 0 && <button onClick={handleMarkRead} className="text-xs text-purple-700 hover:underline">Mark all read</button>}
          </div>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Bell size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map(n => (
                <div key={n.id} className={`p-3 rounded-xl border text-sm ${n.is_read ? 'bg-gray-50 border-gray-100' : 'bg-purple-50 border-purple-100'}`}>
                  <p className="font-semibold text-gray-800">{n.title}</p>
                  <p className="text-gray-600 text-xs mt-0.5">{n.message}</p>
                  <p className="text-gray-400 text-xs mt-1">{n.created_at ? format(new Date(n.created_at), 'MMM d, h:mm a') : ''}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
